/**
 * RankingsLayout
 *
 * Standalone layout shell for the Rankings page.
 * Uses the modern atlas UI system — no Redux, no lexicon runtime, no GlobalGrid.
 */

import React, { lazy, Suspense } from "react";
import styled from "@emotion/styled";
import { useLocation, useSearchParams } from "react-router";
import { typedUseParams } from "../../routing/typedRoutes";
import NavigationBar from "../../components/navigation/ContentPageNav";
import Footer from "../../components/footer/Footer";
import { lightThemeOverride } from "@atlas-int/tokens";
import { Spinner } from "@atlas-int/ui";
import { RouteID } from "../../routing/routes";
import Rankings from "./index";

const GrowthProjectionsPressRelease = lazy(
  () => import("./growthProjections/growthProjectionsPressRelease/index"),
);

const LightThemeScope = styled.div`
  ${lightThemeOverride}
`;

const LayoutContainer = styled.div`
`;

const ContentArea = styled.main`
`;

const LoadingFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
`;

/**
 * Determines the route ID from the current URL pathname.
 */
function useRankingsRouteID(): RouteID {
  const location = useLocation();
  const pathname = location.pathname;

  if (
    pathname === "/growth-projections/data" ||
    pathname.startsWith("/rankings/growth-projections")
  ) {
    return RouteID.GrowthProjections;
  }
  if (
    pathname.startsWith("/press-release") ||
    pathname === "/growth-projections"
  ) {
    return RouteID.GrowthProjectionsPressRelease;
  }
  if (pathname.startsWith("/rankings/product")) {
    return RouteID.ProductRankings;
  }
  return RouteID.CountryRankings;
}

const RankingsLayout: React.FC = () => {
  const routeID = useRankingsRouteID();
  // Typed params from the /rankings/:year route pattern
  const params = typedUseParams("/rankings/:year");
  const [searchParams] = useSearchParams();

  // Convert to the pathParams/queryParams format used by Rankings
  const pathParams: Record<string, string | undefined> = { ...params };
  const queryParams: Record<string, string | undefined> = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  let content: React.ReactElement;
  if (routeID === RouteID.GrowthProjectionsPressRelease) {
    content = (
      <Suspense
        fallback={
          <LoadingFallback>
            <Spinner size="lg" />
          </LoadingFallback>
        }
      >
        <GrowthProjectionsPressRelease />
      </Suspense>
    );
  } else {
    content = (
      <Rankings
        routeID={
          routeID as
            | RouteID.CountryRankings
            | RouteID.ProductRankings
            | RouteID.GrowthProjections
        }
        pathParams={pathParams}
        queryParams={queryParams}
      />
    );
  }

  return (
    <LightThemeScope>
      <LayoutContainer>
        <NavigationBar isHomePage={false} />
        <ContentArea>{content}</ContentArea>
        <Footer />
      </LayoutContainer>
    </LightThemeScope>
  );
};

export default RankingsLayout;
